import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@material-ui/core";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { authToken } from "../components/Authentication";
import { BackendURL } from "../globals";
import { ProjectExtended } from "./ProjectEditDialog"; //TODO decouple
import ScheduleIcon from "@material-ui/icons/Schedule";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import moment from "moment";
import LogsView from "../components/LogsView";
import { Alert } from "@material-ui/lab";
import analytics from "../analytics";

type DeploymentStatus =
  | "PENDING"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "FAILED"
  | "DESTROYING"
  | "DESTROYED";

export type DeploymentJob = {
  pk: number;
  deployment_type: "INFRASTRUCTURE" | "SOFTWARE";
  environment_name: string;
  status: DeploymentStatus;
  start_time?: string;
  service_name?: string;
  branch?: string;
  commit?: string;
};

export default function DeploymentsPage(props: { project: ProjectExtended }) {
  const apiRoot = `${BackendURL}/api`;
  axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

  const [deployments, setDeployments] = useState([] as DeploymentJob[]);
  const [openDeployment, setOpenDeployment] = useState(
    null as DeploymentJob | null
  );

  useEffect(() => {
    analytics.page("Deployments");
  }, []);

  useEffect(() => {
    async function fetchDeployments() {
      const url = `${apiRoot}/projects/${props.project.name}/deployments/`;
      //const url = "https://run.mocky.io/v3/bae77a79-4257-43ac-bed5-7264e50bd906";
      const response = await axios.get(url);
      setDeployments(response.data.results);
    }
    fetchDeployments();
    const interval = setInterval(fetchDeployments, 5000);
    return () => clearInterval(interval);
  }, [apiRoot, props.project.name]);

  return (
    <>
      <Grid container justify="center" spacing={1}>
        {deployments.map((deployment) => (
          <Grid item xs={8} key={deployment.pk}>
            <Card variant="outlined">
              <CardContent>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item>
                    <StatusIcon deployment={deployment} />
                  </Grid>
                  <Grid item xs={1}>
                    #{deployment.pk}
                  </Grid>
                  <Grid item xs={2}>
                    Environment: <strong>{deployment.environment_name}</strong>
                  </Grid>
                  <Grid item xs={2}>
                    Type: <strong>{deployment.deployment_type}</strong>
                  </Grid>

                  <Grid item xs={2}>
                    {deployment.service_name && (
                      <div>
                        Service: <strong>{deployment.service_name}</strong>
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={deployment.status === "PENDING"}
                      onClick={() => setOpenDeployment(deployment)}
                    >
                      View logs
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    style={{
                      display: "flex",
                      flexGrow: 1,
                      justifyContent: "flex-end",
                    }}
                  >
                    {deployment.start_time
                      ? moment(deployment.start_time).fromNow()
                      : "pending"}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={8}>
          <Alert severity="info">
            After you create an Environment, Digger starts 2 deployment jobs.{" "}
            <br />
            The first is an Infrastructure deployment that sets up everything in
            your AWS account. <br />
            The second is a Software deployment that will start after the
            infrastructure is ready and deploy your code. <br />
            <strong>This may take a few minutes</strong>
          </Alert>
        </Grid>
      </Grid>
      <DeploymentLogsDialog
        open={!!openDeployment}
        deployment={openDeployment}
        projectName={props.project.name}
        onCancel={() => setOpenDeployment(null)}
      />
    </>
  );

  function StatusIcon({ deployment }: { deployment: DeploymentJob }) {
    //TODO colors
    if (deployment.status === "PENDING") {
      return <ScheduleIcon color="disabled" />;
    } else if (deployment.status === "COMPLETED") {
      return <CheckCircleIcon style={{ color: "#00c853" }} />;
    } else if (
      deployment.status === "FAILED" ||
      moment().diff(deployment.start_time, "minutes") >= 60
    ) {
      return <CancelIcon style={{ color: "#d32f2f" }} />;
    } else if (deployment.status === "IN_PROGRESS") {
      return <CircularProgress size={20} color="primary" />;
    } else {
      return null;
    }
  }
}

function DeploymentLogsDialog(props: {
  open: boolean;
  deployment: DeploymentJob | null;
  projectName: string;
  onCancel: () => any;
}) {
  const apiRoot = `${BackendURL}/api`;
  axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;

  const logsUrl = `${apiRoot}/projects/${props.projectName}/deployments/${props.deployment?.pk}/logs/`;
  //const logsUrl = "https://run.mocky.io/v3/1bf62167-7993-4473-81e5-ee55342c75f9";
  return (
    <Dialog open={props.open} fullWidth maxWidth={false}>
      <DialogTitle>Deployment logs</DialogTitle>
      <DialogContent>
        <LogsView url={logsUrl} />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
